import React, { useEffect, useMemo, useRef, useState } from 'react'
import { GRID_CHECKBOX_SELECTION_COL_DEF, gridPaginatedVisibleSortedGridRowIdsSelector, useGridApiRef } from '@mui/x-data-grid'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'
import GridPageSelectionCheckbox from './GridPageSelectionCheckbox'
import GridSelectionActions from './GridSelectionActions'
import GridDeleteRowAction from './GridDeleteRowAction'
import DeleteRowsDialog from './DeleteRowsDialog'

export const getApiErrorMessage = (error, fallback) => {
    const detail = error?.response?.data?.error
    return (typeof detail === 'string' ? detail : detail?.message) || fallback
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

const sameIds = (a, b) => a.length === b.length && a.every((id, index) => id === b[index])

/**
 * Adds checkbox selection, a selection action bar, a per-row delete action and a confirmation
 * dialog to an AppDataGrid whose rows are fully loaded client-side. `rows` must be the currently
 * displayed (filtered) rows; each row must expose its database `id`.
 */
export default function useGridRowDeletion({ enabled = true, rows, getRowId, url, tableLabel, filtered = false, onDeleted }) {
    const [selection, setSelection] = useState([])
    const [deletion, setDeletion] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState('')
    const deletionLock = useRef(false)
    const apiRef = useGridApiRef()
    const [pageIds, setPageIds] = useState([])
    const busy = Boolean(deletion) || deleting

    const rowsById = useMemo(() => new Map(rows.map((row) => [getRowId ? getRowId(row) : row.id, row])), [rows, getRowId])
    const selectedRows = useMemo(() => selection.map((id) => rowsById.get(id)).filter(Boolean), [selection, rowsById])

    // Tracks the rows of the visible page (after sorting and pagination) to offer "Select all" like SAP Direct.
    useEffect(() => {
        const api = apiRef.current
        if (!enabled || typeof api?.subscribeEvent !== 'function') return undefined
        const update = () => {
            const ids = gridPaginatedVisibleSortedGridRowIdsSelector(apiRef)
            setPageIds((previous) => (sameIds(previous, ids) ? previous : ids))
        }
        update()
        return api.subscribeEvent('stateChange', update)
    }, [apiRef, enabled])

    const selectedIds = useMemo(() => new Set(selection), [selection])
    const pageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
    const allSelected = selectedRows.length === rows.length && rows.length > pageIds.length && pageIds.length > 0

    // Rows hidden by filters or removed from the table can never be deleted through a stale selection.
    useEffect(() => {
        setSelection((previous) => {
            const next = previous.filter((id) => rowsById.has(id))
            return next.length === previous.length ? previous : next
        })
    }, [rowsById])

    const openDeletion = (targetRows, single = false) => {
        if (busy || targetRows.length === 0) return
        setError('')
        setDeletion({ rows: targetRows, single })
    }

    const closeDeletion = () => {
        if (deleting) return
        setDeletion(null)
        setError('')
    }

    const confirmDeletion = async () => {
        if (!deletion || deletionLock.current) return
        deletionLock.current = true
        setDeleting(true)
        setError('')
        const ids = deletion.rows.map((row) => row.id)
        try {
            const { data } = await ApiService(url, 'delete', { ids }, null, 0)
            if (data?.deletedCount !== ids.length) throw new Error('Unexpected deletion result. Reload the page before deleting again.')
            const deletedIds = new Set(ids)
            setSelection((previous) => previous.filter((id) => !deletedIds.has(rowsById.get(id)?.id)))
            setDeletion(null)
            onDeleted?.(deletedIds)
        } catch (err) {
            setError(getApiErrorMessage(err, err.message || 'Unable to delete the selected rows.'))
        } finally {
            deletionLock.current = false
            setDeleting(false)
        }
    }

    const count = deletion?.rows.length || 0
    const togglePage = (select) => {
        if (busy) return
        setSelection((previous) => {
            const page = new Set(pageIds)
            const kept = previous.filter((id) => !page.has(id))
            return select ? [...kept, ...pageIds] : kept
        })
    }
    const checkboxColumn = {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        renderHeader: () => <GridPageSelectionCheckbox pageIds={pageIds} selectedIds={selectedIds} disabled={busy} onChange={togglePage} />,
    }
    const actionsColumn = {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 90,
        getActions: (params) => [
            <GridDeleteRowAction key='delete' label='Delete row' disabled={busy} onClick={() => openDeletion([params.row], true)} />,
        ],
    }

    if (!enabled) return { gridProps: {}, withActionsColumn: (columns) => columns, dialog: null }

    return {
        gridProps: {
            apiRef,
            checkboxSelection: true,
            disableRowSelectionOnClick: true,
            rowSelectionModel: selection,
            onRowSelectionModelChange: (ids) => {
                if (!busy) setSelection(ids)
            },
            showToolbar: true,
            toolbarLeft: (
                <GridSelectionActions
                    count={selectedRows.length}
                    total={rows.length}
                    filtered={filtered}
                    canSelectAll={pageSelected && selectedRows.length < rows.length}
                    allSelected={allSelected}
                    disabled={busy}
                    onClear={() => setSelection([])}
                    onSelectAll={() => setSelection([...rowsById.keys()])}
                    onDelete={() => openDeletion(selectedRows)}
                />
            ),
        },
        withActionsColumn: (columns) => [checkboxColumn, ...columns, actionsColumn],
        dialog: deletion ? (
            <DeleteRowsDialog
                open
                title={deletion.single ? `Delete this row from ${tableLabel}?` : `Delete ${plural(count, 'row')} from ${tableLabel}?`}
                message={`${deletion.single ? 'This row' : `The ${plural(count, 'selected row')}`} will be permanently removed. Unselected rows are not affected.`}
                warning='Quotations will no longer find a price for deleted rows until they are imported again.'
                deleting={deleting}
                error={error}
                onClose={closeDeletion}
                onConfirm={confirmDeletion}
            />
        ) : null,
    }
}
